// =============================================================
//  CenterDetailScreen — Détail d'un centre (école)
// =============================================================

import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

import AdminLayout from './web-ui/AdminLayout';
import { Card, Button, Badge, Avatar, Divider } from './web-ui/kit';
import { colors, spacing } from './web-ui/theme';

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('fr-FR');
};

export default function CenterDetailScreen({ route, navigation }) {
  const initial = route?.params?.center || {};
  const [center, setCenter] = useState(initial);
  const [loading, setLoading] = useState(false);

  const isBlocked = center.status === 'blocked';
  const name = center.centerName || center.name || 'Sans nom';

  const toggleBlock = async () => {
    setLoading(true);
    try {
      const newStatus = isBlocked ? 'active' : 'blocked';
      await updateDoc(doc(db, 'centers', center.id), { status: newStatus });
      setCenter((p) => ({ ...p, status: newStatus }));
    } catch (e) {
      console.log('toggle center error', e?.message || e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title={name} subtitle="Détail du centre">
      <View style={styles.actions}>
        <Button variant="secondary" icon="arrow-left" onPress={() => navigation.goBack()}>Retour</Button>
        <Button
          variant={isBlocked ? 'primary' : 'danger'}
          icon={isBlocked ? 'lock-open-outline' : 'lock-outline'}
          loading={loading}
          onPress={toggleBlock}
        >
          {isBlocked ? 'Débloquer' : 'Bloquer'}
        </Button>
      </View>

      <Card>
        <View style={{ flexDirection: 'row', gap: spacing.lg, alignItems: 'center' }}>
          <Avatar src={center.photoURL || center.imageUrl} name={name} size={72} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.title}>{name}</Text>
            <Text style={styles.subtitle}>{center.email || '—'}</Text>
            <View style={{ flexDirection: 'row', gap: 6, marginTop: 8 }}>
              <Badge variant={isBlocked ? 'danger' : 'success'}>
                {isBlocked ? 'Bloqué' : 'Actif'}
              </Badge>
              <Badge variant="primary">centre</Badge>
            </View>
          </View>
        </View>

        <Divider />

        <View style={styles.grid}>
          <InfoItem label="Email" value={center.email} />
          <InfoItem label="Téléphone" value={center.phoneNumber} />
          <InfoItem label="Ville" value={center.city || center.region} />
          <InfoItem label="Adresse" value={center.address} />
          <InfoItem label="Site web" value={center.website} />
          <InfoItem label="Inscription" value={fmtDate(center.createdAt)} />
        </View>

        {!!center.description && (
          <>
            <Divider />
            <Text style={styles.sectionTitle}>À propos</Text>
            <Text style={styles.body}>{center.description}</Text>
          </>
        )}
      </Card>
    </AdminLayout>
  );
}

function InfoItem({ label, value }) {
  return (
    <View style={styles.infoItem}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue} numberOfLines={1}>{value || '—'}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginBottom: spacing.md,
    flexWrap: 'wrap',
  },
  title: { fontSize: 20, fontWeight: '700', color: colors.text },
  subtitle: { fontSize: 13, color: colors.textSecondary, marginTop: 4 },
  sectionTitle: { fontSize: 14, fontWeight: '700', color: colors.text, marginBottom: spacing.sm },
  body: { fontSize: 14, color: colors.textSecondary, lineHeight: 21 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md },
  infoItem: {
    flex: 1,
    minWidth: 200,
    backgroundColor: colors.surfaceAlt,
    padding: spacing.md,
    borderRadius: 10,
  },
  infoLabel: { fontSize: 11, color: colors.textMuted, fontWeight: '600', textTransform: 'uppercase' },
  infoValue: { fontSize: 14, color: colors.text, fontWeight: '600', marginTop: 4 },
});
